import { Minus, Plus } from 'lucide-react'

/* Small controls shared by the admin panels. Everything here is operable with
   a D-pad alone: toggles, steppers and segmented choices — no free typing. */

export function AdminToggle({ label, detail, value, onChange, disabled }: {
  label: string
  detail?: string
  value: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="admin-row">
      <div className="admin-row-copy">
        <h3>{label}</h3>
        {detail ? <p>{detail}</p> : null}
      </div>
      <button
        className={`toggle${value ? ' on' : ''}`}
        data-focusable
        disabled={disabled}
        onClick={() => onChange(!value)}
        aria-label={label}
        aria-pressed={value}
      ><i /></button>
    </div>
  )
}

export function AdminStepper({ label, detail, value, min, max, step = 1, format, onChange }: {
  label: string
  detail?: string
  value: number
  min: number
  max: number
  step?: number
  format?: (value: number) => string
  onChange: (value: number) => void
}) {
  const clamp = (next: number) => Math.max(min, Math.min(max, Math.round(next * 100) / 100))
  return (
    <div className="admin-row">
      <div className="admin-row-copy">
        <h3>{label}</h3>
        {detail ? <p>{detail}</p> : null}
      </div>
      <div className="admin-stepper" data-nav-group="row" data-nav-key={`stepper:${label}`}>
        <button
          className="stepper-button"
          data-focusable
          disabled={value <= min}
          onClick={() => onChange(clamp(value - step))}
          aria-label={`Decrease ${label}`}
        ><Minus size="1.5rem" /></button>
        <span className="stepper-value">{format ? format(value) : value}</span>
        <button
          className="stepper-button"
          data-focusable
          disabled={value >= max}
          onClick={() => onChange(clamp(value + step))}
          aria-label={`Increase ${label}`}
        ><Plus size="1.5rem" /></button>
      </div>
    </div>
  )
}

export function AdminChoice<T extends string>({ label, detail, value, options, onChange }: {
  label: string
  detail?: string
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
}) {
  return (
    <div className="admin-row admin-row-stack">
      <div className="admin-row-copy">
        <h3>{label}</h3>
        {detail ? <p>{detail}</p> : null}
      </div>
      <div className="admin-choice" data-nav-group="row" data-nav-key={`choice:${label}`}>
        {options.map((option) => (
          <button
            key={option.value}
            className={`filter-button${option.value === value ? ' active' : ''}`}
            data-focusable
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
          >{option.label}</button>
        ))}
      </div>
    </div>
  )
}

export function AdminSwatches({ label, detail, value, options, onChange }: {
  label: string
  detail?: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (value: string) => void
}) {
  return (
    <div className="admin-row admin-row-stack">
      <div className="admin-row-copy">
        <h3>{label}</h3>
        {detail ? <p>{detail}</p> : null}
      </div>
      <div className="admin-swatches" data-nav-group="row" data-nav-key="swatches">
        {options.map((option) => (
          <button
            key={option.value}
            className={`swatch${option.value.toLowerCase() === value.toLowerCase() ? ' active' : ''}`}
            data-focusable
            onClick={() => onChange(option.value)}
            aria-label={option.label}
            aria-pressed={option.value.toLowerCase() === value.toLowerCase()}
          >
            <i style={{ background: option.value }} />
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
